import { useTranslation } from "react-i18next"
import { Power, Trash2, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"

export interface GlassesSelectionToolbarProps {
  selectedCount: number
  allSelected: boolean
  indeterminate: boolean
  onToggleAll: (checked: boolean) => void
  onTurnOn: () => void
  onDelete: () => void
  onClear: () => void
  canTurnOn: boolean
  turningOn?: boolean
  deleting?: boolean
}

export const GlassesSelectionToolbar = ({
  selectedCount,
  allSelected,
  indeterminate,
  onToggleAll,
  onTurnOn,
  onDelete,
  onClear,
  canTurnOn,
  turningOn,
  deleting,
}: GlassesSelectionToolbarProps) => {
  const { t } = useTranslation()

  return (
    <tr className="border-b bg-vto-accent-soft" data-testid="glasses-selection-toolbar">
      <td className="h-10 px-3 py-0 align-middle [&:has([role=checkbox])]:pr-0">
        <Checkbox
          data-testid="glasses-selection-toggle-all"
          aria-label="Toggle all"
          checked={indeterminate ? "indeterminate" : allSelected}
          onCheckedChange={(value) => onToggleAll(Boolean(value))}
        />
      </td>
      <td colSpan={4} className="h-10 px-3 py-0 align-middle">
        <div className="flex h-10 items-center gap-3">
          <span className="text-sm font-medium text-foreground">
            {t("glasses.selection.count", { count: selectedCount })}
          </span>
          <span className="h-5 w-px bg-border" aria-hidden />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 gap-1.5 px-2 text-xs text-foreground hover:bg-background/60"
            disabled={!canTurnOn || turningOn}
            onClick={onTurnOn}
            data-testid="glasses-selection-turn-on"
          >
            <Power className="size-3.5" />
            {t("glasses.selection.turnOn")}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 gap-1.5 px-2 text-xs text-destructive hover:bg-destructive/10 hover:text-destructive"
            disabled={deleting}
            onClick={onDelete}
            data-testid="glasses-selection-delete"
          >
            <Trash2 className="size-3.5" />
            {t("glasses.selection.delete")}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="ml-auto h-7 gap-1.5 px-2 text-xs text-muted-foreground"
            onClick={onClear}
            data-testid="glasses-selection-clear"
          >
            <X className="size-3.5" />
            {t("glasses.selection.clear")}
          </Button>
        </div>
      </td>
    </tr>
  )
}
